import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { apiRequest, authService } from '../utils/auth';
import { toast } from 'sonner';
import { Download, Upload, Database, Shield, User, Building } from 'lucide-react';
import Layout from '../components/Layout';

export default function Settings() {
  const [loading, setLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const userData = authService.getUser();

  const handleDownloadBackup = async () => {
    setLoading(true);
    try {
      const token = authService.getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/backup`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download backup');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${userData?.company_code || 'data'}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Backup downloaded successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to download backup');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a valid JSON backup file');
      return;
    }
    
    setRestoreLoading(true);
    try {
      const token = authService.getToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to restore backup');
      }
      
      const result = await response.json();
      toast.success(`Backup restored! Customers: ${result.restored.customers}, RFQs: ${result.restored.rfqs}, Quotations: ${result.restored.quotation_costings}`);
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      toast.error(error.message || 'Failed to restore backup');
    } finally {
      setRestoreLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="settings-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-500">Manage your account and data</p>
          </div>
        </div>

        {/* User Info Card */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold">Account Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Username</p>
              <p className="font-medium">{userData?.username || '-'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Role</p>
              <p className="font-medium capitalize">{userData?.role || '-'}</p>
            </div>
            {userData?.company_code && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Company Code</p>
                <p className="font-medium">{userData.company_code}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Backup & Restore Card */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold">Data Backup & Restore</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Download a backup of your company's data or restore from a previous backup file.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Download Backup */}
            <div className="p-6 border border-gray-200 rounded-lg bg-gradient-to-br from-blue-50 to-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Download className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold">Download Backup</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Export all your company data including customers, RFQs, and quotations to a JSON file.
              </p>
              <Button 
                onClick={handleDownloadBackup}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="download-backup-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span> Preparing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Download className="w-4 h-4" /> Download Backup
                  </span>
                )}
              </Button>
            </div>

            {/* Restore Backup */}
            <div className="p-6 border border-gray-200 rounded-lg bg-gradient-to-br from-green-50 to-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Upload className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-semibold">Restore Backup</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Import data from a previously downloaded backup file. Existing data will not be overwritten.
              </p>
              <label className="block">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleRestoreBackup}
                  disabled={restoreLoading}
                  className="hidden"
                  data-testid="restore-backup-input"
                />
                <Button 
                  as="span"
                  disabled={restoreLoading}
                  className="w-full bg-green-600 hover:bg-green-700 cursor-pointer"
                  onClick={() => document.querySelector('[data-testid="restore-backup-input"]').click()}
                  data-testid="restore-backup-btn"
                >
                  {restoreLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">⏳</span> Restoring...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Upload className="w-4 h-4" /> Upload & Restore
                    </span>
                  )}
                </Button>
              </label>
            </div>
          </div>

          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Backups include Customers, Potential Customers, RFQs, and Quotation Costings. 
              Uploaded files and attachments are not included in backups.
            </p>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
